import { defineStore } from 'pinia';
import {
  getUserIdAPI,
  QRVerifyAPI,
  getUser,
  getUserPreferences,
  getFeaturesAPI,
  loginAPI,
  resendPwdAPI,
  setPreferredVehicleAPI,
  registerAPI,
  changePwAPI,
  setOptinAPI,
  getOptinAPI
} from '@/api/user';
import {
  AuthStore,
  UserFeatureExtended,
  ExtendedAxiosError,
  RegisterUserT,
  OPTin
} from './types';
import { useLoyaltyStore } from '@/store/loyalty/loyalty';
import { useHistoryStore } from '@/store/history/history';
import { AxiosError } from 'axios';

import { setUserId, setUserProperties } from "firebase/analytics";
import { analytics } from "@/firebase";

const setCredentials = (token: string, userId: string) => {
  console.log('Setting credentials in localStorage', token, userId);

  const expiry = new Date();
  expiry.setMonth(expiry.getMonth() + 1);

  const saveObj = {
    expiry: expiry.getTime(),
    token,
    userId
  };
  localStorage.setItem('auth', JSON.stringify(saveObj));
};

const getCredentialsFromLocalstorage = () => {
  const storageToken = localStorage.getItem('auth');

  if (!storageToken) {
    return null;
  }

  const saveObj = JSON.parse(storageToken.replace(/\s/g, ''));
  if (new Date().getTime() > saveObj.expiry) {

    localStorage.removeItem('auth');
    return null;
  }
  return { token: saveObj.token, userId: saveObj.userId };
};

export const useAuthStore = defineStore('auth', {
  state: (): AuthStore => ({
    authToken: null,
    accountId: null,
    userId: null,
    email: null,
    lang: null,
    userCurrency: null,
    userPreferences: null,
    preferredVehicle: null,
    userFeatures: [],
    isLoading: false,
    tokenIsInvalid: false,
    customTheme: null,
  }),
  actions: {
    async saveToken(token: string, lang: string) {

      this.authToken = token;
      this.lang = lang || 'en';
    },
    async getUserId() {
      const userSimpleData = await getUserIdAPI();
      this.userId = userSimpleData[0]._id;
    },
    async saveAuthData(userId: string = null, account: string = null, email: string = null, currency: string = null) {

      // if there's no email or currency in the URL, we eed to retrieve them from /users/{:userId}
      // if the userId is missing too, we need to retrieve it from /users
      if (!email || !currency) {
        userId
          ? this.userId = userId
          : await this.getUserId();
        await this.getUserAccountEmailCurrency();
      } else {
        this.userId = userId,
          this.email = email,
          this.userCurrency = currency,
          this.accountId = account;
      }
      if (!this.userPreferences?.length) await this.storeUserPreferences();

      if (analytics) {
        setUserId((analytics), userId);
        setUserProperties((analytics), {
          account_id: account,
        });
      }
    },

    async getUserAccountEmailCurrency() {
      try {
        const userData = await getUser(this.userId as string);
        this.accountId = userData.accounts[0].id;
        this.userCurrency = userData.localisation.currency;
        this.email = userData.email;

      } catch (error) {
        console.log('check Auth Status', error);
      } finally {
        // commit('ui/setLoading', false, { root: true });
      }
    },
    async storeUserPreferences() {
      this.isLoading = true;

      try {
        const res = await getUserPreferences();
        if (!res.thingAvatars.length) throw new Error('things empty');
        this.userPreferences = res.thingAvatars;
        await this.setPreferredVehicle(res.preferredThing);

      } catch (error) {
        console.log('Get preferences error', error);
        if ([401, 403].includes((error as AxiosError).response?.status)) {
          this.tokenIsInvalid = true;
          throw 'unauthorized';
        }
      } finally {
        this.isLoading = false;
      }
    },
    async getUserFeatures(accountId: string | null = null, retry = 0) {
      const loyaltyState = useLoyaltyStore();

      try {

        const features = await getFeaturesAPI(accountId);
        this.userFeatures = features.filter(feat => feat.status === 'ENABLED');

        const loyaltyFeat: UserFeatureExtended = this.userFeatures.find(
          (feat: UserFeatureExtended) => feat.feature === 'loyaltyPrograms');

        loyaltyFeat && loyaltyState.setLoyaltyPrograms(loyaltyFeat.programs);
      } catch (error: any) {

        // if accountId in URL is wrong or absent AND retry is 0 (avoid infinite recursion)
        if (error.response?.status === 401 && retry === 0) {
          console.log(retry);

          try {
            await this.getUserId();
            await this.getUserAccountEmailCurrency();
            await this.getUserFeatures(this.accountId, 1);
          } catch (error) {
            console.log('Get user data or features error', error);
            throw error;
          }
          return;
        }

        console.log('Get user features error', error);
        throw new Error(error);
      }
    },
    async setPreferredVehicle(thingId: string) {

      if (!this.userPreferences?.length) {
        try {
          await this.storeUserPreferences();
        } catch (error) {
          console.log('Get user preferences error', error);
          throw error;
        }

      }

      const vehicleFromParam = thingId
        ? this.userPreferences.find(item => item.thingId === thingId)
        : this.userPreferences[0];

      const historyState = useHistoryStore();

      historyState.thingIdToShowEvents = vehicleFromParam.thingId;
      historyState.thingToCreateEvent = vehicleFromParam;

      this.preferredVehicle = vehicleFromParam;

    },
    async patchPreferredVehicle(thingId: string) {

      try {
        await setPreferredVehicleAPI(thingId);
        await this.setPreferredVehicle(thingId);

      } catch (error) {
        console.log('Patch user preferences error', error);
        throw error;
      }

    },
    async login(email: string, password: string) {
      try {
        const authData = await loginAPI(email, password);
        this.authToken = authData.auth_token;
        setCredentials(authData.auth_token, authData._id);

        if (authData.passwordExpired) {
          throw new Error('temp_pass');
        }
      } catch (error) {
        if (
          (error as ExtendedAxiosError).response?.data.errorCode === 125
          || (error as ExtendedAxiosError).response?.data.errorCode === 127
        ) {
          throw new Error('wrong_login_details');
        } else {
          throw error;
        }
      } finally {
      }

    },
    async register(data: RegisterUserT) {
      try {
        const deepLinkString = localStorage.getItem('extraPartnerData');

        await registerAPI({
          ...data,
          ...(deepLinkString && { extraData: JSON.parse(deepLinkString) }),
        });

        // Remove extra-data from localStorage when a success registration is done
        if (!!deepLinkString) {
          localStorage.removeItem('extraPartnerData');
        }

      } catch (error) {
        if ((error as ExtendedAxiosError).response?.data?.message.includes('already registered')) {
          throw new Error('email_has_account');
        }
        throw error;
      }
    },
    async changePw(newPassword: string) {
      try {
        await changePwAPI(newPassword);
      } catch (error) {
        console.log('Change password error', error);
        throw error;
      }
    },
    async forgotPwd(email: string) {
      try {
        await resendPwdAPI(email);
      } catch (error) {
        console.log('Resend password error', error);
      }
    },
    async QRVerify(qrToken: string) {
      try {
        await QRVerifyAPI(qrToken);
      } catch (error) {
        console.log('QR code verify error', error);
        throw error;
      }
    },
    getCredentialsFromLocalstorage() {
      const storageToken = localStorage.getItem('auth');

      if (!storageToken) {
        return null;
      }

      const saveObj = JSON.parse(storageToken.replace(/\s/g, ''));

      if (new Date().getTime() > saveObj.expiry) {

        localStorage.removeItem('auth');
        return null;
      }
      this.authToken = saveObj.token;
      this.userId = saveObj.userId;
    },
    async setOptin(options: OPTin) {
      try {
        await setOptinAPI(options);
      } catch (error) {
        console.error('Error during beta test opt-in:', error);
        throw error;
      }
    },
    async getOptin(name: string): Promise<OPTin> {
      try {
        const response = await getOptinAPI(name);
        return response;
      } catch (error) {
        console.error('Error fetching opt-in status:', error);
        throw error;
      }
    }

  },

});